/**
 * One modifier group on the Item screen, and the groups nested under the
 * choices chosen in it (ORDR-165). Recursive: commerce nests at most three
 * levels (`MAX_MODIFIER_DEPTH`), and a nested group is drawn only while the
 * choice that opens it is chosen.
 *
 * A required group that takes exactly one answer is a radio group. Every other
 * group is checkboxes, so an optional single answer can be taken off again,
 * and an unchosen box is disabled once the group is at its maximum. A choice
 * the chosen size does not price is disabled and says so.
 */

import type { ReactElement } from "react";
import { formatMinor } from "../customer/money.js";
import type { CatalogueChoice, CatalogueGroup } from "./catalogue.js";
import { boundsLabel, choiceDelta, groupKey, takesOne, type Picks } from "./selection.js";

export interface OptionGroupProps {
  readonly group: CatalogueGroup;
  readonly groupKey: string;
  readonly picks: Picks;
  readonly currency: string;
  /** `null` until the diner chooses a size, or for an item sold in one size. */
  readonly portion: { readonly code: string; readonly name: string } | null;
  readonly portioned: boolean;
  readonly onPick: (key: string, group: CatalogueGroup, choiceId: string, on: boolean) => void;
}

function deltaText(choice: CatalogueChoice, props: OptionGroupProps): { text: string; unpriced: boolean } {
  const delta = choiceDelta(choice, props.portion?.code ?? null, props.portioned);
  if (delta === null) {
    return props.portion === null ? { text: "", unpriced: false } : { text: `Not available in ${props.portion.name}`, unpriced: true };
  }
  if (delta === 0) {
    return { text: "", unpriced: false };
  }
  const magnitude = formatMinor(Math.abs(delta), props.currency);
  return { text: delta > 0 ? `+${magnitude}` : `−${magnitude}`, unpriced: false };
}

export function OptionGroup(props: OptionGroupProps): ReactElement {
  const { group, picks, onPick } = props;
  const key = props.groupKey;
  const chosen = picks[key] ?? [];
  const radio = takesOne(group) && group.min >= 1;
  const full = !takesOne(group) && chosen.length >= group.max;

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="pb-2 text-base font-bold">
        {group.name} <span className="text-sm font-normal text-[var(--color-neutral-600)]">{boundsLabel(group)}</span>
      </legend>
      {group.choices.map((choice) => {
        const on = chosen.includes(choice.id);
        const delta = deltaText(choice, props);
        return (
          <div key={choice.id} className="flex flex-col gap-2">
            <label
              className={`flex min-h-12 items-center gap-3 rounded-[10px] px-3.5 text-base ${
                on ? "border-2 border-[var(--color-primary-700)]" : "border border-[var(--color-neutral-300)]"
              }`}
            >
              <input
                type={radio ? "radio" : "checkbox"}
                name={key}
                checked={on}
                disabled={delta.unpriced || (!on && full)}
                onChange={(event) => onPick(key, group, choice.id, radio ? true : event.target.checked)}
                className="h-5 w-5 accent-[var(--color-primary-700)]"
              />
              <span className="grow">{choice.name}</span>{" "}
              <span className="text-[var(--color-neutral-600)]">{delta.text}</span>
            </label>
            {on && choice.child !== null && (
              <div className="ml-4 border-l-2 border-[var(--color-neutral-200)] pl-3">
                <OptionGroup {...props} group={choice.child} groupKey={groupKey(key, choice.child.id, choice.id)} />
              </div>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
